import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Home, Briefcase, FileText, FolderCheck, User, Upload, Bell, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import PortalShell from "./PortalShell";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NationalitySelect, OptionSelect, PhoneInput, GENDERS, MARITAL, EDUCATION, EMPLOYMENT, splitPhone, isValidPhone, todayISO, toInputDate } from "@/components/FormControls";
import { Progress } from "@/components/ui/progress";

const NAV = [
  { to: "/app/candidate", end: true, label: "Home", icon: Home },
  { to: "/app/candidate/jobs", label: "Jobs", icon: Briefcase },
  { to: "/app/candidate/applications", label: "Applications", icon: FileText },
  { to: "/app/candidate/documents", label: "Documents", icon: FolderCheck },
  { to: "/app/candidate/profile", label: "Profile", icon: User },
];

const STAGES = ["Registration", "Documents", "Screening", "Interview", "Selection", "Offer",
  "Medical", "MOL / Contract", "Visa / Processing", "Joining / Ticket", "Deployment"];

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { client.get("/me/candidate/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-slate-400">Loading...</div>;
  const c = data.candidate;
  const stageIdx = STAGES.indexOf(c.current_stage);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm text-slate-400">{c.code}</div>
            <h2 className="font-display text-2xl font-bold text-[#0A192F]">{c.full_name}</h2>
            <div className="mt-1"><StatusBadge value={c.current_stage} kind="stage" /></div>
          </div>
          <div className="w-full sm:w-48">
            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Profile</span><span>{c.profile_completion || 0}%</span></div>
            <Progress value={c.profile_completion || 0} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Applications", value: data.applications.length },
          { label: "Documents", value: data.documents.length },
          { label: "Pending Docs", value: data.pending_documents },
          { label: "Interviews", value: data.upcoming_interviews.length },
        ].map((k) => (
          <div key={k.label} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-display text-3xl font-bold text-[#0A192F]">{k.value}</div>
            <div className="text-xs text-slate-500 mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-[#0A192F] mb-4">Your Recruitment Journey</h3>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s, i) => (
            <div key={s} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
              i < stageIdx ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
              i === stageIdx ? "bg-[#0088FF] text-white border-[#0088FF]" :
              "bg-slate-50 text-slate-400 border-slate-200"}`}>
              {i < stageIdx && <CheckCircle2 className="h-3.5 w-3.5" />} {s}
            </div>
          ))}
        </div>
      </div>

      {data.upcoming_interviews.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-semibold text-[#0A192F] mb-3">Upcoming Interviews</h3>
          {data.upcoming_interviews.map((iv) => (
            <div key={iv.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
              <div><div className="font-medium text-sm">{iv.job_title}</div><div className="text-xs text-slate-400">{new Date(iv.date).toLocaleString()}</div></div>
              <StatusBadge value={iv.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const nav = useNavigate();
  useEffect(() => { client.get("/public/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);
  const apply = async (id) => {
    try { await client.post(`/me/candidate/apply/${id}`); toast.success("Applied successfully!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
      {jobs.map((j) => (
        <div key={j.id} className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-semibold text-[#0A192F]">{j.title}</h3>
          <p className="text-sm text-slate-500">{j.client_name} · {j.country}</p>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm font-medium text-[#0088FF]">{j.salary}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => nav(`/jobs/${j.id}`)}>View</Button>
              <Button size="sm" data-testid={`cand-apply-${j.id}`} onClick={() => apply(j.id)} className="bg-[#0088FF] hover:bg-[#0070E0]">Apply</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  useEffect(() => { client.get("/me/candidate/dashboard").then((r) => setApps(r.data.applications)).catch(() => {}); }, []);
  return (
    <div className="space-y-3 max-w-3xl">
      {apps.length === 0 && <div className="text-slate-400">No applications yet. Browse jobs to apply.</div>}
      {apps.map((a) => (
        <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex justify-between items-center">
          <div><div className="font-semibold text-[#0A192F]">{a.job_title}</div><div className="text-sm text-slate-400">{a.client_name} · {a.country} · {a.code}</div></div>
          <StatusBadge value={a.stage} kind="stage" />
        </div>
      ))}
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState([]);
  const [cand, setCand] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => client.get("/me/candidate/dashboard").then((r) => { setDocs(r.data.documents); setCand(r.data.candidate); });
  useEffect(() => { load(); }, []);

  const upload = async (docType, file) => {
    if (!file || !cand) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("candidate_id", cand.id);
    fd.append("doc_type", docType);
    fd.append("file", file);
    try { await client.post("/documents/upload", fd); toast.success(`${docType} uploaded`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const TYPES = ["Passport", "Photo", "Medical", "Education", "Experience"];
  return (
    <div className="max-w-3xl space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-display font-semibold text-[#0A192F] mb-3">Upload Documents</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {TYPES.map((t) => (
            <label key={t} className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer hover:border-[#0088FF]" data-testid={`upload-${t.toLowerCase()}`}>
              <span className="text-sm text-slate-600 flex items-center gap-2"><Upload className="h-4 w-4" /> {t}</span>
              <input type="file" className="hidden" disabled={uploading} onChange={(e) => upload(t, e.target.files[0])} />
            </label>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-display font-semibold text-[#0A192F] mb-3">My Documents</h3>
        {docs.length === 0 && <div className="text-slate-400 text-sm">No documents uploaded.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
            <span className="text-sm text-slate-700">{d.doc_type}</span>
            <StatusBadge value={d.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Profile() {
  const [c, setC] = useState(null);
  useEffect(() => { client.get("/me/candidate/dashboard").then((r) => { const d = r.data.candidate; setC({ ...d, phone: splitPhone(d.mobile, d.mobile_code) }); }).catch(() => {}); }, []);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setV = (k) => (v) => setC({ ...c, [k]: v });
  const save = async () => {
    if (!isValidPhone(c.phone.number)) return toast.error("Enter a valid phone number (6 to 14 digits)");
    const { phone, ...rest } = c;
    try { await client.put("/me/candidate/profile", { ...rest, mobile: phone.number, mobile_code: phone.code }); toast.success("Profile updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (!c) return <div className="text-slate-400">Loading...</div>;
  const txt = (k) => <Input data-testid={`profile-${k}`} value={c[k] || ""} onChange={set(k)} />;
  const F = [
    ["full_name", "Full Name", txt("full_name")], ["passport_number", "Passport Number", txt("passport_number")],
    ["passport_expiry_date", "Passport Expiry", <Input data-testid="profile-passport_expiry_date" type="date" value={toInputDate(c.passport_expiry_date)} onChange={set("passport_expiry_date")} />],
    ["nationality", "Nationality", <NationalitySelect testId="profile-nationality" value={c.nationality} onChange={setV("nationality")} />],
    ["date_of_birth", "Date of Birth", <Input data-testid="profile-date_of_birth" type="date" max={todayISO()} value={toInputDate(c.date_of_birth)} onChange={set("date_of_birth")} />],
    ["gender", "Gender", <OptionSelect testId="profile-gender" value={c.gender} onChange={setV("gender")} options={GENDERS} />],
    ["marital_status", "Marital Status", <OptionSelect testId="profile-marital_status" value={c.marital_status} onChange={setV("marital_status")} options={MARITAL} />],
    ["mobile", "Mobile", <PhoneInput testId="profile-mobile" value={c.phone} onChange={setV("phone")} />],
    ["address", "Address", txt("address")], ["position", "Position", txt("position")],
    ["education", "Education Level", <OptionSelect testId="profile-education" value={c.education} onChange={setV("education")} options={EDUCATION} />],
    ["employment_status", "Employment Status", <OptionSelect testId="profile-employment_status" value={c.employment_status} onChange={setV("employment_status")} options={EMPLOYMENT} />],
  ];
  return (
    <div className="max-w-2xl bg-white rounded-2xl border border-slate-200 p-6">
      <div className="grid sm:grid-cols-2 gap-4">
        {F.map(([k, l, el]) => <div key={k}><Label>{l}</Label>{el}</div>)}
      </div>
      <Button onClick={save} data-testid="profile-save" className="mt-5 bg-[#0088FF] hover:bg-[#0070E0]">Save Profile</Button>
    </div>
  );
}

function Notifications() {
  const [list, setList] = useState([]);
  useEffect(() => { client.get("/me/notifications").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-2xl space-y-2">
      {list.length === 0 && <div className="text-slate-400">No notifications.</div>}
      {list.map((n) => (
        <div key={n.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <Bell className="h-4 w-4 text-[#0088FF]" /><div className="text-sm text-slate-700">{n.title}</div>
        </div>
      ))}
    </div>
  );
}

export default function CandidatePortal() {
  return (
    <PortalShell nav={NAV} roleLabel="Candidate" title="Candidate Portal">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="jobs" element={<JobsTab />} />
        <Route path="applications" element={<Applications />} />
        <Route path="documents" element={<Documents />} />
        <Route path="profile" element={<Profile />} />
        <Route path="notifications" element={<Notifications />} />
      </Routes>
    </PortalShell>
  );
}
